import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { colors } from "../../constants/theme";

// ----- Rating section (stars + Toss/Solid/Crave) -----
export function CheckInRatingSection({
  rating,
  onRatingChange,
  /** Stars + word labels; default matches legacy saddle brown. */
  accentColor = colors.saddle,
}) {
  const labelStyle = [sharedStyles.emojiLabelText, { color: accentColor }];
  return (
    <View style={sharedStyles.ratingSection}>
      <View style={sharedStyles.starsContainer}>
        {[1, 2, 3, 4, 5].map((starNumber) => {
          const isSelected = starNumber <= rating;
          return (
            <TouchableOpacity
              key={starNumber}
              style={sharedStyles.starButton}
              onPress={() => onRatingChange(starNumber)}
            >
              <Ionicons
                name={isSelected ? "star" : "star-outline"}
                size={32}
                color={accentColor}
                style={{ opacity: isSelected ? 1 : 0.72 }}
              />
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={sharedStyles.emojiLabelsContainer}>
        <View style={sharedStyles.emojiLabelLeft}>
          <Text style={sharedStyles.emojiLabelEmoji}>🫢</Text>
          <Text style={labelStyle}>Toss</Text>
        </View>
        <View style={sharedStyles.emojiLabelCenter}>
          <Text style={sharedStyles.emojiLabelEmoji}>😌</Text>
          <Text style={labelStyle}>Solid</Text>
        </View>
        <View style={sharedStyles.emojiLabelRight}>
          <Text style={sharedStyles.emojiLabelEmoji}>🤤</Text>
          <Text style={labelStyle}>Crave</Text>
        </View>
      </View>
    </View>
  );
}

// ----- SpeedRailz questions section (Drink Quality, Service, Vibe) -----
const SPEEDRAILZ_INFO = {
  drinkQuality: {
    title: "Drink Quality",
    text:
      "Rate the quality of the drinks you ordered. Consider factors like taste, freshness, " +
      "presentation, and whether the drinks meet your expectations for the price point.",
  },
  serviceHospitality: {
    title: "Service and Hospitality",
    text:
      "Rate the service you received from staff. Consider factors like friendliness, " +
      "attentiveness, knowledge of the menu, speed of service, and overall hospitality.",
  },
  vibeAtmosphere: {
    title: "Vibe and Atmosphere",
    text:
      "Rate the overall atmosphere and vibe of the venue. Consider factors like music, " +
      "lighting, crowd energy, decor, cleanliness, and whether the space feels welcoming " +
      "and matches the type of experience you're looking for.",
  },
};

function SpeedRailzRow({ title, value, onValueChange, onInfoPress }) {
  return (
    <View style={sharedStyles.speedRailzRatingSection}>
      <View style={sharedStyles.speedRailzRatingHeader}>
        <Text style={sharedStyles.speedRailzRatingTitle}>{title}</Text>
        <TouchableOpacity onPress={onInfoPress} style={sharedStyles.infoButton}>
          <Ionicons name="information-circle-outline" size={20} color="black" />
        </TouchableOpacity>
      </View>
      <View style={sharedStyles.starsContainer}>
        {[1, 2, 3, 4, 5].map((starNumber) => {
          const isSelected = starNumber <= value;
          return (
            <TouchableOpacity
              key={starNumber}
              style={sharedStyles.starButton}
              onPress={() => onValueChange(starNumber)}
            >
              <Ionicons
                name={isSelected ? "star" : "star-outline"}
                size={28}
                color={isSelected ? "#FBBF24" : "#D0D0D0"}
              />
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

export function CheckInSpeedRailzSection({
  isSpeedRailzPartner,
  drinkQuality,
  serviceHospitality,
  vibeAtmosphere,
  onDrinkQualityChange,
  onServiceHospitalityChange,
  onVibeAtmosphereChange,
}) {
  const [infoModal, setInfoModal] = useState(null);

  if (!isSpeedRailzPartner) return null;

  const currentInfo = infoModal ? SPEEDRAILZ_INFO[infoModal] : null;

  return (
    <>
      <SpeedRailzRow
        title="Drink Quality"
        value={drinkQuality}
        onValueChange={onDrinkQualityChange}
        onInfoPress={() => setInfoModal("drinkQuality")}
      />
      <SpeedRailzRow
        title="Service and Hospitality"
        value={serviceHospitality}
        onValueChange={onServiceHospitalityChange}
        onInfoPress={() => setInfoModal("serviceHospitality")}
      />
      <SpeedRailzRow
        title="Vibe and Atmosphere"
        value={vibeAtmosphere}
        onValueChange={onVibeAtmosphereChange}
        onInfoPress={() => setInfoModal("vibeAtmosphere")}
      />

      {currentInfo && (
        <Modal
          visible={!!infoModal}
          transparent
          animationType="fade"
          onRequestClose={() => setInfoModal(null)}
        >
          <View style={sharedStyles.infoModalOverlay}>
            <View style={sharedStyles.infoModalContent}>
              <View style={sharedStyles.infoModalHeader}>
                <Text style={sharedStyles.infoModalTitle}>{currentInfo.title}</Text>
                <TouchableOpacity
                  onPress={() => setInfoModal(null)}
                  style={sharedStyles.closeButton}
                >
                  <Ionicons name="close" size={24} color="#8B4513" />
                </TouchableOpacity>
              </View>
              <Text style={sharedStyles.infoModalText}>{currentInfo.text}</Text>
              <TouchableOpacity
                style={sharedStyles.infoModalButton}
                onPress={() => setInfoModal(null)}
              >
                <Text style={sharedStyles.infoModalButtonText}>Got it</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      )}
    </>
  );
}

const sharedStyles = StyleSheet.create({
  ratingSection: {
    marginHorizontal: 20,
    marginTop: 20,
    alignItems: "center",
  },
  starsContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 5,
  },
  starButton: {
    padding: 8,
    marginHorizontal: 4,
  },
  emojiLabelsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
    paddingHorizontal: 0,
    width: "100%",
  },
  emojiLabelLeft: { alignItems: "center", flex: 1 },
  emojiLabelCenter: { alignItems: "center", flex: 1 },
  emojiLabelRight: { alignItems: "center", flex: 1 },
  emojiLabelEmoji: { fontSize: 24, marginBottom: 5 },
  emojiLabelText: { fontSize: 14, fontWeight: "500" },

  speedRailzRatingSection: {
    marginHorizontal: 20,
    marginTop: 24,
    alignItems: "center",
    backgroundColor: "#FEF9E7",
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderWidth: 2,
    borderColor: "#FBBF24",
  },
  speedRailzRatingHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
    gap: 8,
  },
  speedRailzRatingTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "black",
    textAlign: "center",
  },
  infoButton: { padding: 4 },

  infoModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  infoModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    width: "85%",
    maxWidth: 400,
  },
  infoModalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  infoModalTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "black",
    flex: 1,
  },
  infoModalText: {
    fontSize: 15,
    color: "black",
    lineHeight: 22,
    marginBottom: 20,
  },
  closeButton: { padding: 4 },
  infoModalButton: {
    backgroundColor: "#FBBF24",
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: "center",
  },
  infoModalButtonText: {
    color: "black",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default CheckInRatingSection;
